import { useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { format, isPast } from "date-fns";

import { Modal } from "@/components/Modals/Modal";
import {
	usePauseScheduleMutation,
	useResumeScheduleMutation,
} from "@/pages/Private/redux/schedule/schedule.api";
import { ListPersonSchedule } from "@/pages/Private/pages/List/schema/list";
import { useSnackBar } from "@/utils/useSnackbar";

/**
 * Confirm-and-pause / confirm-and-resume flow for one manual follow-up (PREC-2861), the sibling of
 * `useCancelScheduleModal`. The direction comes from the row itself (`active`), so the button that
 * opened the modal and the request it sends can never disagree.
 *
 * The resume confirmation names when the message will go out: the send time is kept, and a time
 * already past means the next send pass picks the message up — a user who paused a follow-up
 * last week must not learn that by watching it leave.
 */
export const usePauseScheduleModal = () => {
	const { t } = useTranslation();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	// The schedule pending confirmation; null when the modal is closed.
	const [schedule, setSchedule] = useState<ListPersonSchedule | null>(null);
	const [pauseSchedule, { isLoading: isPausing }] = usePauseScheduleMutation();
	const [resumeSchedule, { isLoading: isResuming }] = useResumeScheduleMutation();

	const direction = schedule?.active ? "pause" : "resume";

	const handleConfirm = useCallback(async () => {
		if (!schedule) {
			return;
		}

		try {
			if (schedule.active) {
				await pauseSchedule(schedule.id).unwrap();
				handleOpenSnackbar(t("outbox.pauseSchedule.pausedSuccess"));
			} else {
				await resumeSchedule(schedule.id).unwrap();
				handleOpenSnackbar(t("outbox.pauseSchedule.resumedSuccess"));
			}
		} catch {
			handleOpenSnackbar(t(`outbox.pauseSchedule.${direction}Error`));
		} finally {
			setSchedule(null);
		}
	}, [schedule, direction, pauseSchedule, resumeSchedule, handleOpenSnackbar, t]);

	const resumeConfirmation = (): string => {
		if (!schedule?.scheduledFor) {
			return t("outbox.pauseSchedule.resumeConfirmationNow");
		}

		const scheduledFor = new Date(schedule.scheduledFor);

		return isPast(scheduledFor)
			? t("outbox.pauseSchedule.resumeConfirmationPast", {
					date: format(scheduledFor, "HH:mm dd.MM.yyyy"),
				})
			: t("outbox.pauseSchedule.resumeConfirmationFuture", {
					date: format(scheduledFor, "HH:mm dd.MM.yyyy"),
				});
	};

	const renderPauseScheduleModal = () => (
		<>
			{schedule !== null && (
				<Modal
					handleClose={() => setSchedule(null)}
					handleSave={handleConfirm}
					isLoading={isPausing || isResuming}
					isOpened={schedule !== null}
					size="xs"
					submitButtonText={t(`outbox.pauseSchedule.${direction}Action`)}
					title={t(`outbox.pauseSchedule.${direction}Title`)}
				>
					<div className="text-sm text-gray-700" data-testid="pause-schedule-confirmation">
						{direction === "pause"
							? t("outbox.pauseSchedule.pauseConfirmation")
							: resumeConfirmation()}
					</div>
				</Modal>
			)}
			{renderMessageSnackbar()}
		</>
	);

	return { requestPauseOrResume: setSchedule, renderPauseScheduleModal };
};
